import React, { useMemo, useState, useCallback } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import useDarkMode from '../../hooks/useDarkMode';
import './PhoneNumberInput.css';

const digitsOnly = (val) => String(val || '').replace(/\D/g, '');

const stripDialCodePrefix = (digits, dialCode) => {
	if (!dialCode) return digits;
	const dial = String(dialCode);
	return digits.startsWith(dial) ? digits.slice(dial.length) : digits;
};

const formatE164WithSpace = (dialCode, localDigits) => {
	if (!dialCode) return String(localDigits || '');
	const local = String(localDigits || '');
	return local ? `+${dialCode} ${local}` : `+${dialCode}`;
};

const normalizeSriLankaLocalDigits = (localDigits) => {
	let local = String(localDigits || '');
	if (local.startsWith('0')) local = local.slice(1);
	return local.slice(0, 9);
};

/**
 * PhoneNumberInput
 * A wrapper around react-phone-input-2 that exposes a simple API and works with our forms.
 * Props:
 * - value: string (e.g., '+44 7700 900123')
 * - onChange: (value: string, country: { dialCode?: string; countryCode?: string }) => void
 * - defaultCountryIso: string (e.g., 'gb') to set initial country
 * - disabled: boolean
 * - inputProps: object passed to the underlying input
 */
const PhoneNumberInput = ({
	value,
	onChange,
	defaultCountryIso,
	disabled = false,
	inputProps = {},
}) => {
	const { darkModeStatus } = useDarkMode();
	const country = useMemo(
		() => (defaultCountryIso ? defaultCountryIso.toLowerCase() : undefined),
		[defaultCountryIso],
	);

	const [lastDialCode, setLastDialCode] = useState(undefined);

	// compute colours/styles so disabled state appears greyed-out
	const disabledBg = darkModeStatus ? '#21252975' : '#f8f9fa'; // light grey used when disabled
	const disabledText = '#6c757d';
	const defaultBg = darkModeStatus ? '#212529' : '#f8f9fa';
	const defaultText = darkModeStatus ? '#fff' : '#323232';
	const inputBg = disabled ? disabledBg : defaultBg;
	const inputColor = disabled ? disabledText : defaultText;

	const handleChange = useCallback(
		(val, ctry, e, formattedValue) => {
			const dial = ctry?.dialCode ? String(ctry.dialCode) : '';
			if (dial) setLastDialCode(dial);

			const isSriLanka =
				dial === '94' || String(ctry?.countryCode || '').toLowerCase() === 'lk';

			// Prefer the formatted value (includes `+` and spacing) since callers in this codebase
			// generally treat the value as `+<dial> <local>`.
			const raw = formattedValue || val || '';

			if (isSriLanka) {
				const rawDigits = digitsOnly(raw);
				const localDigits = stripDialCodePrefix(rawDigits, dial || '94');
				const normalizedLocal = normalizeSriLankaLocalDigits(localDigits);
				const normalized = formatE164WithSpace(dial || '94', normalizedLocal);
				if (onChange) onChange(normalized, ctry, e, normalized);
				return;
			}

			if (onChange) onChange(raw, ctry, e, formattedValue);
		},
		[onChange],
	);

	const isCodeSelected = useMemo(() => {
		const hasAnyValue = typeof value === 'string' && value.trim().length > 0;
		return Boolean(country) || Boolean(lastDialCode) || hasAnyValue;
	}, [country, lastDialCode, value]);

	const containerClass = `sd-phone-input${isCodeSelected ? '' : ' sd-phone-empty'}`;

	const handleKeyDown = useCallback(
		(e) => {
			const userOnKeyDown = inputProps?.onKeyDown;
			if (typeof userOnKeyDown === 'function') userOnKeyDown(e);
			if (e.defaultPrevented) return;

			// Only apply the strict 9-digit restriction when Sri Lanka (+94) is selected.
			const selectedIsSriLanka =
				country === 'lk' ||
				lastDialCode === '94' ||
				String(value || '').replace(/\s/g, '').startsWith('+94');
			if (!selectedIsSriLanka) return;

			// Let browser/OS shortcuts and navigation keys through.
			if (e.ctrlKey || e.metaKey || e.altKey) return;
			const allowed = new Set([
				'Backspace',
				'Delete',
				'Tab',
				'ArrowLeft',
				'ArrowRight',
				'ArrowUp',
				'ArrowDown',
				'Home',
				'End',
				'Enter',
			]);
			if (allowed.has(e.key)) return;

			// Only block additional numeric input when the local part already has 9 digits.
			if (!/^\d$/.test(e.key)) return;

			const current = e.currentTarget?.value ?? value ?? '';
			const currentDigits = digitsOnly(current);
			const localDigits = normalizeSriLankaLocalDigits(
				stripDialCodePrefix(currentDigits, '94'),
			);

			const selectionStart = e.currentTarget?.selectionStart;
			const selectionEnd = e.currentTarget?.selectionEnd;
			const hasSelection =
				typeof selectionStart === 'number' &&
				typeof selectionEnd === 'number' &&
				selectionEnd > selectionStart;

			if (!hasSelection && localDigits.length >= 9) {
				e.preventDefault();
			}
		},
		[country, inputProps, lastDialCode, value],
	);

	return (
		<PhoneInput
			country={country}
			value={value}
			onChange={handleChange}
			disabled={disabled}
			containerClass={containerClass}
			buttonClass='sd-phone-flag'
			dropdownClass='sd-phone-dropdown'
			searchClass='sd-phone-search sd-phone-search-box'
			inputClass={'form-control'}
			inputProps={{
				name: 'phone',
				required: false,
				autoFocus: false,
				...inputProps,
				onKeyDown: handleKeyDown,
			}}
			enableSearch
			countryCodeEditable={false}
			placeholder={''}
			containerStyle={{ width: '100%' }}
			inputStyle={{
				width: '100%',
				background: inputBg,
				color: inputColor,
				cursor: disabled ? 'not-allowed' : 'text',
				borderRadius: '1rem',
				minHeight: '47.5px',
				border: darkModeStatus ? '1px solid #353b3fff' : '1px solid #ecececc9',
				boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.075)',
			}}
			buttonStyle={{
				borderTopLeftRadius: '1rem',
				borderBottomLeftRadius: '1rem',
				background: disabled ? disabledBg : defaultBg,
				border: darkModeStatus ? '1px solid #353b3fff' : '1px solid #ecececc9',
				cursor: disabled ? 'not-allowed' : 'pointer',
			}}
			dropdownStyle={{
				background: darkModeStatus ? '#212529' : '#fff',
				color: darkModeStatus ? '#fff' : '#323232',
				borderRadius: '1rem',
				border: darkModeStatus ? '1px solid #5c5d5f' : '1px solid #ecececc9',
			}}
		/>
	);
};

export default PhoneNumberInput;
